/* eslint-disable @typescript-eslint/no-unused-vars */
import { cloneElement, isValidElement, type Key, type ReactNode } from "react";
import {
  Dropdown as AntDropdown,
  type DropdownProps as AntDropdownProps,
  type MenuProps as AntMenuProps,
} from "antd";

import type { AvE2EProps } from "../types";
import type { ItemType as AntItemType } from "antd/es/menu/interface";
import type { MenuDividerType } from "antd/lib/menu/interface";
import type { MenuClickEventHandler } from "rc-menu/lib/interface";

export type MenuItemType = Omit<AntItemType, "label" | "children"> & {
  testIdPrefix?: string;
  isHidden?: boolean;
  children?: ItemType[];
  label: ReactNode;
  key: Key;
  onClick?: MenuClickEventHandler;
  icon?: ReactNode;
  disabled?: boolean;
};

export type ItemType = MenuDividerType | MenuItemType;

interface MenuProps extends Omit<AntMenuProps, "items"> {
  items?: ItemType[];
}
interface DropdownProps extends AvE2EProps, Omit<AntDropdownProps, "menu"> {
  menu?: MenuProps;
}

/**
 * Recursively remove isHidden items from the menu items
 */
function getVisibleItems(
  items: ItemType[] | undefined,
  dropdownTestIdPrefix: string
): AntItemType[] {
  if (!items) return [];

  return items
    .filter((item) => {
      if (!("isHidden" in item)) {
        return true;
      }

      return !item.isHidden;
    })
    .map((item) => {
      if ("type" in item && item.type === "divider") {
        return item as AntItemType;
      }

      const { key, isHidden, label, children, testIdPrefix, ...rest } = item as MenuItemType;

      const sanitizedChildren = getVisibleItems(children, dropdownTestIdPrefix);

      return {
        ...rest,
        key,
        label: (
          <span data-testid={`${dropdownTestIdPrefix}-${testIdPrefix ?? key}-item`}>{label}</span>
        ),
        ...(sanitizedChildren.length > 0 ? { children: sanitizedChildren as AntItemType[] } : {}),
      } as AntItemType;
    });
}

export function Dropdown(props: DropdownProps) {
  const { testIdPrefix, menu, children, ...rest } = props;

  const visibleItems = getVisibleItems(menu?.items, testIdPrefix);

  if (visibleItems.length === 0) return children;

  let clonedChildren = children;

  if (isValidElement(children)) {
    clonedChildren = cloneElement(children, {
      ["data-testid"]: `${testIdPrefix}-dropdown`,
    } as typeof children.props & { ["data-testid"]: string });
  } else {
    throw new Error("Dropdown children contains invalid props.");
  }

  const { items: _omit, ...menuProps } = menu || {};

  return (
    <AntDropdown
      menu={{
        ...menuProps,
        items: visibleItems,
      }}
      {...rest}
    >
      {clonedChildren}
    </AntDropdown>
  );
}
